import React, { useState, useContext, useMemo } from 'react';

import { Row, Col, notification } from 'antd';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';

import styled from 'styled-components';

import { getMoment } from '@libs/NwMoment';
import { datePickerBeforeOnChange, getNow, toISODate } from '@libs/NwMoment';

import { ListContext } from '@contexts/ListContext';
import { UserContext } from '@contexts/UserContext';

import { useSaveAddressOnContact } from '@hooks/API/Contact/UseContactConnections';
import { getContactName } from '@hooks/API/Contact/UseContactsList';
import { useUpdateScoutingAccommodation, useAddScoutingAccommodation } from '@hooks/API/Event/UseScoutingAccommodation';
import { useTravelPlan } from '@hooks/API/UseTravelPlan';

import FlexContainer from '@components/Gui/FlexContainer';
import { NwCancelButton, NwSaveButton } from '@components/Gui/NwButton';
import NwDivider from '@components/Gui/NwDivider';
import {
    NWInput,
    NWSelect,
    NWContactDropdown,
    NWTextAreaCharCounter,
    NWDatePicker,
    NWLocationSelector,
} from '@components/Gui/NwForm/NwFormItems';
import { NwForm, NwFormButtonsBar } from '@components/Gui/NwForm/NwFormWrapper';

const CheckDate = styled.div`
    margin: 2px 8px 0 0;
`;

const ScoutingAccommodationForm = (props) => {
    const {
        scoutingAccommodationData,
        viewMode,
        travelPlanId,
        afterUpdate,
        onCancel,
        onUpdateScoutingAccommodationData,
        onTouchForm,
    } = props;
    const { data: currentTravelPlan } = useTravelPlan(travelPlanId);
    const { mutateAsync: onAddScoutingAccommodation } = useAddScoutingAccommodation();
    const { mutateAsync: onUpdateScoutingAccommodation } = useUpdateScoutingAccommodation();

    // indirizzo da salvare anche sull'anagrafica del service, se l'utente lo chiede
    const [addLocationToCustomer, setAddLocationToCustomer] = useState(null);
    const saveAddressOnContact = useSaveAddressOnContact();
    const { cachedList } = useContext(ListContext);
    const { currentUser } = useContext(UserContext);
    const users = cachedList.users.items.filter((user) => currentUser.isSuperAdmin || !user.isSuperAdmin);

    const bookers = useMemo(() => {
        let bookers = [];
        if (currentTravelPlan) {
            for (const tripBooker of currentTravelPlan.Bookers) {
                const booker = users.find((user) => user.ID === tripBooker.BookerID);
                if (booker) {
                    bookers.push({
                        value: booker.ID,
                        key: booker.ID,
                        label: `${booker.Name} ${booker.Surname}`,
                    });
                }
            }
        }
        return bookers;

    }, [currentTravelPlan]);

    const submitForm = async (values, setSubmitting) => {
        const addressOnContactError = await saveAddressOnContact({
            addLocationToCustomer,
            address: values.Address,
        });
        if (addressOnContactError) {
            notification.error({ message: 'Error', description: addressOnContactError });
        }

        const newAccommodationData = {
            ...values,
            ...values.Address,
            PhoneNumber: values.Address.Phone,
            UserID: currentTravelPlan.Bookers.length === 1 ? currentTravelPlan.Bookers[0].BookerID : values.UserID,
        };

        delete newAccommodationData.Address;

        if (!scoutingAccommodationData) {
            try {
                const newAccommodation = await onAddScoutingAccommodation({
                    scoutingAccommodationData: newAccommodationData,
                });
                onTouchForm(false);
                if (afterUpdate) {
                    afterUpdate({
                        ...newAccommodation,
                        TypeName: 'Accommodation',
                    });
                }
                onUpdateScoutingAccommodationData(newAccommodation.ID);
                onCancel();
            } catch (error) {
                console.error('ant : Create Scouting Accommodation Error', error);
            }
            setSubmitting(false);
        } else {
            newAccommodationData.ID = scoutingAccommodationData.ID;

            try {
                await onUpdateScoutingAccommodation({ scoutingAccommodationData: newAccommodationData });
                onTouchForm(false);
                if (afterUpdate) {
                    afterUpdate();
                }
                onUpdateScoutingAccommodationData();
                if (!viewMode || viewMode === 'create') {
                    onCancel();
                }
            } catch (error) {
                console.error('ant : Update Scouting Accommodation Error', error);
            }
            setSubmitting(false);
        }
    };

    let initialValues = {
        TravelPlanID: travelPlanId,
        Title: scoutingAccommodationData ? scoutingAccommodationData.Title : '',
        ServiceID: scoutingAccommodationData ? scoutingAccommodationData.ServiceID : '',
        Note: scoutingAccommodationData ? scoutingAccommodationData.Note : '',
        FromDate: scoutingAccommodationData ? scoutingAccommodationData.FromDate : getNow(true),
        ToDate: scoutingAccommodationData ? scoutingAccommodationData.ToDate : getNow(true),
        UserID: scoutingAccommodationData ? scoutingAccommodationData.UserID : undefined,
        Address: {
            Country: scoutingAccommodationData ? scoutingAccommodationData.Country : '',
            City: scoutingAccommodationData ? scoutingAccommodationData.City : '',
            County: scoutingAccommodationData ? scoutingAccommodationData.County : '',
            State: scoutingAccommodationData ? scoutingAccommodationData.State : '',
            Zip: scoutingAccommodationData ? scoutingAccommodationData.Zip : '',
            Street: scoutingAccommodationData ? scoutingAccommodationData.Street : '',
            Access: scoutingAccommodationData ? scoutingAccommodationData.Access : '',
            Phone: scoutingAccommodationData ? scoutingAccommodationData.PhoneNumber : '',
        },
    };

    const disableCheckOutDate = (beforeDate) => (date) => {
        if (beforeDate && toISODate(beforeDate) > toISODate(date)) {
            return true;
        }
        return false;
    };

    return (
        <div className="event-form">
            <Formik
                initialValues={initialValues}
                validationSchema={Yup.object().shape({
                    Title: Yup.string().required('Content is required'),
                    ServiceID: Yup.number().required('Content is required'),
                    UserID: bookers.length > 1 && Yup.string().required('Content is required'),
                })}
                onSubmit={(values, { setSubmitting }) => {
                    submitForm(values, setSubmitting);
                }}
                render={({ isSubmitting, values, handleSubmit, setFieldValue, validateField }) => (
                    <NwForm values={values} onTouchForm={onTouchForm} small onFinish={handleSubmit} layout="vertical">
                        <Row gutter={16}>
                            <Col lg={12} md={12} sm={12} xs={24}>
                                <Field
                                    component={NWContactDropdown}
                                    label={`Service`}
                                    name="ServiceID"
                                    activeTypes={['srv']}
                                    activableTypes={[]}
                                    value={values.ServiceID}
                                    onAfterChange={(serviceId) => {
                                        setFieldValue('Title', getContactName(serviceId));
                                    }}
                                />
                            </Col>
                            {bookers.length > 1 && (
                                <Col lg={12} md={12} sm={12} xs={24}>
                                    <Field
                                        component={NWSelect}
                                        label="Booker"
                                        name="UserID"
                                        value={values.UserID}
                                        options={bookers}
                                    />
                                </Col>
                            )}
                        </Row>
                        <Row gutter={16}>
                            <Col lg={12} md={12} sm={12} xs={24}>
                                <Field
                                    component={NWInput}
                                    label="Title"
                                    name="Title"
                                    type="text"
                                    value={values.Title}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <FlexContainer>
                                <FlexContainer alignItems="flex-start" mg="0 16px 0 0">
                                    <CheckDate>Check In</CheckDate>
                                    <Field
                                        component={NWDatePicker}
                                        name="FromDate"
                                        format="MMM DD, YYYY"
                                        getCalendarContainer={(triggerNode) => triggerNode.parentNode}
                                        style={{ width: '100%' }}
                                        value={values.FromDate ? getMoment(values.FromDate) : null}
                                        manipulateValue={datePickerBeforeOnChange}
                                        onAfterChange={(date) => setFieldValue('ToDate', date)}
                                    />
                                </FlexContainer>

                                <FlexContainer alignItems="flex-start">
                                    <CheckDate>Check Out</CheckDate>
                                    <Field
                                        component={NWDatePicker}
                                        // label="Check"
                                        name="ToDate"
                                        format="MMM DD, YYYY"
                                        getCalendarContainer={(triggerNode) => triggerNode.parentNode}
                                        style={{ width: '100%' }}
                                        value={values.ToDate ? getMoment(values.ToDate) : null}
                                        manipulateValue={datePickerBeforeOnChange}
                                        disabledDate={disableCheckOutDate(values.FromDate)}
                                    />
                                </FlexContainer>
                            </FlexContainer>
                        </Row>
                        <NwDivider>Address</NwDivider>
                        <Field
                            component={NWLocationSelector}
                            name="Address"
                            value={values.Address}
                            eventType="accommodation"
                            customerId={values.ServiceID}
                            clearOnChange={scoutingAccommodationData ? false : true}
                            addLocationToCustomer={addLocationToCustomer}
                            setAddLocationToCustomer={setAddLocationToCustomer}
                        />
                        <Field
                            component={NWTextAreaCharCounter}
                            label="Note"
                            name="Note"
                            type="text"
                            value={values.Note}
                            maxLength={2000}
                            autoSize={{ minRows: 4, maxRows: 10 }}
                        />
                        <NwFormButtonsBar
                            left={
                                <NwCancelButton
                                    disabled={isSubmitting}
                                    onClick={() => {
                                        if (onTouchForm) {
                                            onTouchForm(false);
                                        }
                                        onCancel();
                                    }}
                                />
                            }
                            right={<NwSaveButton htmlType="submit" disabled={isSubmitting} loading={isSubmitting} />}
                        />
                    </NwForm>
                )}
            />
        </div>
    );
};

export default React.memo(ScoutingAccommodationForm);
